/**
 * Responsibility: show who is signed in, and let them leave.
 *
 * Lives in the shell's `topBarActions` slot rather than the sidebar's small
 * print, because that slot takes a `ReactNode` and the small print takes
 * strings - and this needs a button. The vendored kit is a faithful upstream
 * copy and is not edited to add a slot when a usable one already exists.
 *
 * The avatar falls back to the first letter of the address. A Google account
 * without a photo is ordinary, and a broken image icon in the top bar is worse
 * than a letter.
 */
import { useState, type MouseEvent } from 'react';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import LogoutIcon from '@mui/icons-material/LogoutOutlined';
import { useAuth } from './authContext';

const AVATAR = 32;

export function AccountMenu() {
  const { user, signOut } = useAuth();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  if (!user) return null;

  const open = (event: MouseEvent<HTMLElement>) => setAnchor(event.currentTarget);
  const close = () => setAnchor(null);

  return (
    <>
      <IconButton
        onClick={open}
        size="small"
        aria-label={`Signed in as ${user.email}`}
        sx={{ p: 0.5 }}
      >
        <Avatar
          src={user.photoURL ?? undefined}
          alt={user.email}
          sx={{ width: AVATAR, height: AVATAR, fontSize: 14 }}
        >
          {user.email.slice(0, 1).toUpperCase()}
        </Avatar>
      </IconButton>

      <Menu
        anchorEl={anchor}
        open={anchor !== null}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 240 } } }}
      >
        <Stack spacing={0.25} sx={{ px: 2, py: 1 }}>
          {user.displayName ? (
            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
              {user.displayName}
            </Typography>
          ) : null}
          <Typography variant="caption" color="text.secondary" noWrap>
            {user.email}
          </Typography>
        </Stack>

        <Divider />

        <MenuItem
          onClick={() => {
            close();
            void signOut();
          }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Sign out
        </MenuItem>
      </Menu>
    </>
  );
}
